import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password, totpCode, recoveryCode) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    if (recoveryCode) payload.recovery_code = recoveryCode;
    const { data } = await api.post("/auth/login", payload);
    localStorage.setItem("token", data.token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data.user);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("token", data.token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    // Ensure the full user shape (incl. `onboarded`) is populated so first-run flows work.
    await fetchMe();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* best-effort */ }
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("device_token");
    setUser(null);
    window.location.href = "/login";
  };

  const refreshUser = fetchMe;

  // Derived flags every consumer wants. `isOwner` mirrors the backend's
  // OWNER_EMAILS-driven flag; `isPaid` is true for owner OR any active
  // paid subscription (plus / elite / pro). Consumers should use these
  // instead of inspecting user.plan directly so future plan renames
  // (e.g. "pro" → "plus") don't require touching every gate.
  const isOwner = !!user?.is_owner;
  const plan = user?.plan || null;
  const isPaid = isOwner
    || plan === "elite" || plan === "plus" || plan === "pro"
    || !!(user?.subscription && user.subscription.active);

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, login, register, logout, refreshUser,
      isOwner, isPaid, plan,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
